import styled from "styled-components";

export const chatColors = {
  ink: "#0f172a",
  muted: "#64748b",
  faint: "#94a3b8",
  border: "#e2e8f0",
  surface: "#ffffff",
  canvas: "#f8fafc",
  sidebar: "#0f172a",
  sidebarMuted: "#94a3b8",
  primary: "#4f46e5",
  primarySoft: "#eef2ff",
  success: "#15803d",
  warning: "#b45309",
  danger: "#b91c1c",
};

export const ChatRoot = styled.div`
  min-height: 100vh;
  display: flex;
  background: ${chatColors.canvas};
  color: ${chatColors.ink};
  font-family: "Pretendard", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;

  @media (prefers-reduced-motion: reduce) {
    &, * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; scroll-behavior: auto !important; }
  }
`;

export const Sidebar = styled.aside<{ $open: boolean }>`
  width: 280px;
  flex: 0 0 280px;
  display: flex;
  flex-direction: column;
  background: ${chatColors.sidebar};
  color: #f8fafc;
  z-index: 20;

  @media (max-width: 860px) {
    position: fixed;
    inset: 0 auto 0 0;
    transform: translateX(${({ $open }) => ($open ? "0" : "-105%")});
    transition: transform 0.22s ease;
    box-shadow: ${({ $open }) => ($open ? "18px 0 50px rgba(15,23,42,.3)" : "none")};
  }
`;

export const SidebarBackdrop = styled.button<{ $open: boolean }>`
  display: none;

  @media (max-width: 860px) {
    display: ${({ $open }) => ($open ? "block" : "none")};
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.42);
    z-index: 10;
  }
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 22px 20px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
`;

export const BrandMark = styled.span`
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: linear-gradient(135deg, #6366f1, #2563eb);
  color: white;
  font-weight: 900;
  letter-spacing: -0.08em;
`;

export const BrandCopy = styled.div`
  strong { display: block; font-size: 16px; letter-spacing: -0.03em; }
  span { display: block; color: ${chatColors.sidebarMuted}; font-size: 11px; margin-top: 2px; }
`;

export const SidebarSection = styled.section`
  padding: 20px 14px 0;
`;

export const SidebarNav = styled.nav`
  display: grid;
  gap: 4px;
  padding: 14px 14px 0;
`;

export const SidebarNavLink = styled.a`
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 0 11px;
  border-radius: 9px;
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 800;
  text-decoration: none;
  &:hover, &:focus-visible, &.active { color: #fff; background: rgba(99,102,241,.34); }
`;

export const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px 9px;
  color: ${chatColors.sidebarMuted};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const SidebarAction = styled.button`
  min-width: 32px;
  min-height: 32px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  color: #cbd5e1;
  background: transparent;
  font-size: 18px;

  &:hover, &:focus-visible { color: white; background: rgba(255,255,255,.1); }
`;

export const RoomList = styled.div`
  display: grid;
  gap: 4px;
`;

export const RoomButton = styled.button<{ $selected: boolean; $archived: boolean }>`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  text-align: left;
  color: ${({ $selected, $archived }) => ($selected ? "#fff" : $archived ? "#64748b" : "#cbd5e1")};
  background: ${({ $selected }) => ($selected ? "rgba(99,102,241,.34)" : "transparent")};
  opacity: ${({ $archived }) => ($archived ? 0.72 : 1)};

  &:hover, &:focus-visible { background: ${({ $selected }) => ($selected ? "rgba(99,102,241,.42)" : "rgba(255,255,255,.08)")}; color: #fff; }
`;

export const RoomHash = styled.span`
  color: #818cf8;
  font-size: 17px;
  font-weight: 800;
`;

export const RoomButtonCopy = styled.span`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
`;

export const ArchivedLabel = styled.span`
  color: #94a3b8;
  font-size: 10px;
  font-weight: 700;
`;

export const UnreadDot = styled.span`
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #818cf8;
`;

export const SidebarBottom = styled.div`
  margin-top: auto;
  padding: 16px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
`;

export const MemberSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const Avatar = styled.span<{ $kind?: string; $small?: boolean }>`
  width: ${({ $small }) => ($small ? "30px" : "38px")};
  height: ${({ $small }) => ($small ? "30px" : "38px")};
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 11px;
  color: white;
  background: ${({ $kind }) => ($kind === "AGENT" ? "#7c3aed" : "#2563eb")};
  font-size: ${({ $small }) => ($small ? "10px" : "12px")};
  font-weight: 900;
`;

export const MemberCopy = styled.div`
  min-width: 0;
  flex: 1;
  strong, span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  strong { font-size: 13px; }
  span { margin-top: 2px; color: ${chatColors.sidebarMuted}; font-size: 11px; }
`;

export const LogoutButton = styled.button`
  min-height: 34px;
  margin-top: 12px;
  width: 100%;
  border: 1px solid rgba(148,163,184,.2);
  border-radius: 9px;
  color: #cbd5e1;
  background: transparent;
  font-size: 12px;
  font-weight: 700;
  &:hover, &:focus-visible { color: white; border-color: rgba(255,255,255,.42); }
`;

export const Main = styled.main`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const Topbar = styled.header`
  min-height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px clamp(18px, 4vw, 40px);
  border-bottom: 1px solid ${chatColors.border};
  background: rgba(255,255,255,.94);
`;

export const TopbarLeft = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const MobileMenu = styled.button`
  display: none;
  min-width: 38px;
  min-height: 38px;
  border-radius: 10px;
  color: ${chatColors.ink};
  background: ${chatColors.canvas};
  font-size: 20px;
  @media (max-width: 860px) { display: grid; place-items: center; }
`;

export const TopbarCopy = styled.div`
  min-width: 0;
  h1 { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: clamp(17px, 2.4vw, 22px); letter-spacing: -0.04em; }
  p { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: ${chatColors.muted}; font-size: 12px; }
`;

export const TopbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const OutlineButton = styled.button`
  min-height: 38px;
  padding: 0 13px;
  border: 1px solid ${chatColors.border};
  border-radius: 10px;
  color: ${chatColors.muted};
  background: ${chatColors.surface};
  font-size: 12px;
  font-weight: 800;
  &:hover, &:focus-visible { border-color: #a5b4fc; color: ${chatColors.primary}; background: ${chatColors.primarySoft}; }
`;

export const Content = styled.div`
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const CenterState = styled.div`
  min-height: 0;
  flex: 1;
  display: grid;
  place-items: center;
  padding: 32px;
  text-align: center;
  color: ${chatColors.muted};
`;

export const StateCard = styled.section`
  width: min(100%, 500px);
  padding: 32px;
  border: 1px solid ${chatColors.border};
  border-radius: 20px;
  background: ${chatColors.surface};
  box-shadow: 0 20px 60px rgba(15,23,42,.06);
  h1 { margin-bottom: 8px; color: ${chatColors.ink}; font-size: 24px; letter-spacing: -0.04em; }
  p { line-height: 1.7; }
`;

export const ErrorNotice = styled.div`
  margin: 16px clamp(18px, 4vw, 40px) 0;
  padding: 11px 13px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: ${chatColors.danger};
  background: #fff1f2;
  font-size: 13px;
  font-weight: 700;
`;

export const Messages = styled.div`
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 24px clamp(18px, 5vw, 58px) 18px;
  scroll-behavior: smooth;
`;

export const LoadOlderButton = styled.button`
  display: block;
  min-height: 36px;
  margin: 0 auto 18px;
  padding: 0 13px;
  border: 1px solid ${chatColors.border};
  border-radius: 999px;
  color: ${chatColors.primary};
  background: ${chatColors.surface};
  font-size: 12px;
  font-weight: 800;
`;

export const NewMessageButton = styled.button`
  position: sticky;
  bottom: 12px;
  display: block;
  min-height: 36px;
  margin: 0 auto;
  padding: 0 14px;
  border-radius: 999px;
  color: white;
  background: ${chatColors.primary};
  box-shadow: 0 8px 20px rgba(79,70,229,.25);
  font-size: 12px;
  font-weight: 800;
`;

export const MessageRow = styled.article`
  display: flex;
  gap: 12px;
  padding: 11px 0;
  &:hover { background: linear-gradient(90deg, rgba(238,242,255,.42), transparent); }
`;

export const MessageBody = styled.div`
  min-width: 0;
  flex: 1;
`;

export const MessageHeader = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 3px;
  strong { font-size: 14px; }
  time { color: ${chatColors.faint}; font-size: 11px; }
`;

export const RoleBadge = styled.span<{ $kind?: string; $task?: boolean }>`
  padding: 2px 6px;
  border-radius: 999px;
  color: ${({ $task }) => ($task ? "#92400e" : "#4338ca")};
  background: ${({ $task }) => ($task ? "#fef3c7" : "#eef2ff")};
  font-size: 10px;
  font-weight: 800;
`;

export const BodyText = styled.p`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: #334155;
  font-size: 14px;
  line-height: 1.7;
`;

export const ReplyQuote = styled.button`
  max-width: 100%;
  display: block;
  margin-bottom: 6px;
  padding: 7px 10px;
  border-left: 3px solid #a5b4fc;
  border-radius: 4px 8px 8px 4px;
  color: ${chatColors.muted};
  background: ${chatColors.primarySoft};
  text-align: left;
  font-size: 12px;
`;

export const MessageActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 7px;
  opacity: 0;
  transition: opacity .15s ease;
  ${MessageRow}:hover & { opacity: 1; }
  @media (max-width: 860px) { opacity: 1; }
`;

export const TinyButton = styled.button`
  min-height: 30px;
  padding: 0 9px;
  border: 1px solid ${chatColors.border};
  border-radius: 8px;
  color: ${chatColors.muted};
  background: white;
  font-size: 11px;
  font-weight: 700;
`;

export const ComposerWrap = styled.div`
  position: relative;
  padding: 14px clamp(18px, 5vw, 58px) 20px;
  border-top: 1px solid ${chatColors.border};
  background: ${chatColors.surface};
`;

export const ReplyPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 9px;
  padding: 8px 10px;
  border-radius: 9px;
  color: ${chatColors.muted};
  background: ${chatColors.primarySoft};
  font-size: 12px;
`;

export const ComposerToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const Select = styled.select`
  min-height: 32px;
  padding: 0 9px;
  border: 1px solid ${chatColors.border};
  border-radius: 8px;
  color: ${chatColors.muted};
  background: white;
  font-size: 12px;
  font-weight: 700;
`;

export const ComposerHint = styled.span`
  color: ${chatColors.faint};
  font-size: 11px;
`;

export const TaskFields = styled.div`
  display: grid;
  gap: 8px;
  margin-bottom: 9px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  @media (max-width: 680px) { grid-template-columns: 1fr; }
`;

export const TaskField = styled.input`
  min-height: 38px;
  padding: 8px 11px;
  border: 1px solid ${chatColors.border};
  border-radius: 9px;
  color: ${chatColors.ink};
  background: ${chatColors.canvas};
  font-size: 12px;
  outline: none;
  &:focus { border-color: #a5b4fc; box-shadow: 0 0 0 4px rgba(99,102,241,.1); }
`;

export const ComposerForm = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 10px;
`;

export const Textarea = styled.textarea`
  min-height: 52px;
  max-height: 150px;
  flex: 1;
  resize: vertical;
  padding: 13px 14px;
  border: 1px solid ${chatColors.border};
  border-radius: 13px;
  color: ${chatColors.ink};
  background: ${chatColors.canvas};
  font-size: 14px;
  line-height: 1.5;
  outline: none;
  &:focus { border-color: #a5b4fc; box-shadow: 0 0 0 4px rgba(99,102,241,.1); }
`;

export const SendButton = styled.button`
  min-width: 74px;
  min-height: 52px;
  padding: 0 13px;
  border-radius: 13px;
  color: white;
  background: ${chatColors.primary};
  font-size: 13px;
  font-weight: 800;
  &:disabled { cursor: not-allowed; opacity: .55; }
`;

export const JobCards = styled.div`
  display: grid;
  gap: 10px;
  padding: 18px clamp(18px, 5vw, 58px) 0;
`;

export const JobCard = styled.article`
  padding: 14px 16px;
  border: 1px solid #f3d38a;
  border-radius: 13px;
  background: #fffbeb;
  color: #713f12;
  box-shadow: 0 7px 18px rgba(146, 64, 14, .06);
`;

export const JobCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 7px;
  strong { color: #451a03; font-size: 13px; }
`;

export const FakeBadge = styled.span`
  padding: 3px 7px;
  border-radius: 999px;
  color: #92400e;
  background: #fde68a;
  font-size: 10px;
  font-weight: 900;
`;

export const JobCardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  color: #92400e;
  font-size: 11px;
  font-weight: 700;
`;

export const JobCardOutput = styled.p`
  margin-top: 8px;
  color: #78350f;
  font-size: 12px;
  line-height: 1.5;
`;

export const MentionMenu = styled.div`
  position: absolute;
  right: clamp(18px, 5vw, 58px);
  bottom: 142px;
  width: min(300px, calc(100% - 36px));
  max-height: 230px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid ${chatColors.border};
  border-radius: 13px;
  background: white;
  box-shadow: 0 18px 42px rgba(15,23,42,.16);
  z-index: 3;
`;

export const MentionOption = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px;
  border-radius: 9px;
  text-align: left;
  background: ${({ $active }) => ($active ? chatColors.primarySoft : "transparent")};
  &:hover, &:focus-visible { background: ${chatColors.primarySoft}; }
`;

export const MentionsPanel = styled.aside<{ $open: boolean }>`
  width: ${({ $open }) => ($open ? "310px" : "0")};
  flex: 0 0 ${({ $open }) => ($open ? "310px" : "0")};
  overflow: hidden;
  border-left: ${({ $open }) => ($open ? `1px solid ${chatColors.border}` : "none")};
  background: white;
  transition: width .2s ease, flex-basis .2s ease;

  @media (max-width: 1100px) {
    position: fixed;
    inset: 0 0 0 auto;
    width: min(330px, 90vw);
    flex-basis: auto;
    transform: translateX(${({ $open }) => ($open ? "0" : "105%")});
    transition: transform .2s ease;
    z-index: 25;
    box-shadow: ${({ $open }) => ($open ? "-18px 0 50px rgba(15,23,42,.2)" : "none")};
  }
`;

export const PanelInner = styled.div`
  width: 310px;
  padding: 20px 16px;
  @media (max-width: 1100px) { width: min(330px, 90vw); }
`;

export const PanelHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  h2 { font-size: 17px; letter-spacing: -0.03em; }
`;

export const CloseButton = styled.button`
  min-width: 32px;
  min-height: 32px;
  border-radius: 8px;
  color: ${chatColors.muted};
  background: ${chatColors.canvas};
  font-size: 19px;
  &:hover, &:focus-visible { color: ${chatColors.primary}; background: ${chatColors.primarySoft}; }
`;

export const MentionCard = styled.button`
  width: 100%;
  display: block;
  margin-top: 8px;
  padding: 11px;
  border: 1px solid ${chatColors.border};
  border-radius: 12px;
  color: ${chatColors.ink};
  background: white;
  text-align: left;
  &:hover, &:focus-visible { border-color: #a5b4fc; background: ${chatColors.primarySoft}; }
  strong, span { display: block; }
  strong { font-size: 12px; }
  span { margin-top: 4px; color: ${chatColors.muted}; font-size: 12px; line-height: 1.45; }
`;

export const LoginPageRoot = styled.main`
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px;
  background: radial-gradient(circle at 15% 15%, #e0e7ff, transparent 34%), ${chatColors.canvas};
`;

export const LoginCard = styled.section`
  width: min(100%, 440px);
  padding: clamp(28px, 5vw, 46px);
  border: 1px solid ${chatColors.border};
  border-radius: 24px;
  background: white;
  box-shadow: 0 28px 80px rgba(15,23,42,.12);
`;

export const LoginTitle = styled.h1`
  margin: 28px 0 8px;
  color: ${chatColors.ink};
  font-size: clamp(27px, 5vw, 36px);
  line-height: 1.15;
  letter-spacing: -0.05em;
`;

export const LoginDescription = styled.p`
  margin-bottom: 28px;
  color: ${chatColors.muted};
  font-size: 14px;
  line-height: 1.7;
`;

export const LoginForm = styled.form`
  display: grid;
  gap: 15px;
`;

export const Field = styled.label`
  display: grid;
  gap: 7px;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
`;

export const Input = styled.input`
  width: 100%;
  min-height: 48px;
  padding: 0 13px;
  border: 1px solid ${chatColors.border};
  border-radius: 11px;
  color: ${chatColors.ink};
  background: white;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #a5b4fc; box-shadow: 0 0 0 4px rgba(99,102,241,.1); }
`;

export const PrimaryButton = styled.button`
  min-height: 50px;
  margin-top: 5px;
  border-radius: 12px;
  color: white;
  background: ${chatColors.primary};
  font-size: 14px;
  font-weight: 800;
  &:disabled { cursor: not-allowed; opacity: .55; }
`;

export const HelperText = styled.p`
  margin-top: 18px;
  color: ${chatColors.faint};
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
`;

export const CreateRoomForm = styled.form`
  display: grid;
  gap: 10px;
  padding: 12px;
  border: 1px solid rgba(148,163,184,.18);
  border-radius: 12px;
  background: rgba(255,255,255,.05);
  input, textarea { width: 100%; padding: 9px; border: 1px solid rgba(148,163,184,.26); border-radius: 8px; color: white; background: rgba(15,23,42,.4); font: inherit; font-size: 12px; }
  textarea { min-height: 58px; resize: vertical; }
`;

export const MutedText = styled.p`
  color: ${chatColors.muted};
  font-size: 13px;
  line-height: 1.6;
`;

export const SkeletonLine = styled.div<{ $width?: string }>`
  width: ${({ $width }) => $width || "70%"};
  height: 14px;
  margin: 9px 0;
  border-radius: 999px;
  background: #e2e8f0;
  animation: neuralchatPulse 1.3s ease-in-out infinite alternate;
  @keyframes neuralchatPulse { to { opacity: .45; } }
`;
